/**
 * The rain-soaked rooftop arena.
 *
 * Every surface and effect is generated at runtime from geometry, shader code, and labelled RNG
 * forks. Weather derives from simulation time, which keeps captures byte-stable.
 */

import * as THREE from 'three';
import {
  DisposalBag, System, STAGE_HALF_WIDTH, STAGE_HALF_DEPTH, clamp,
} from '../core/index.js';

const RAIN_COUNT = 720;
const RAIN_SPLASH_COUNT = 48;
const STEAM_COUNT = 72;
const CITY_COUNT = 62;
const CROWD_COUNT = 34;
const STEAM_VENTS = Object.freeze([
  Object.freeze([-4.4, 2.5]),
  Object.freeze([4.65, -2.25]),
  Object.freeze([-6.35, -2.72]),
  Object.freeze([6.2, 2.62]),
]);

export class WorldSystem extends System {
  static id = 'world';
  static deps = ['render', 'materials'];
  static budgetMs = 0.45;

  constructor() {
    super();
    this.bag = new DisposalBag('world');
    this.group = new THREE.Group();
    this.group.name = 'arena:rooftop-nine';
    this.scene = null;
    this.rain = null;
    this.rainPositions = null;
    this.rainBase = null;
    this.rainSplashes = null;
    this.rainSplashBase = null;
    this.rainSplashDummy = null;
    this.rainDensity = 1;
    this.steam = null;
    this.steamPositions = null;
    this.steamBase = null;
    this.traffic = [];
    this.bannerMaterials = [];
    this.neonLights = [];
    this.lightning = null;
    this.lightningBolt = null;
    this.lightningMaterial = null;
    this.materials = null;
    this.crowd = null;
    this.crowdRecords = [];
    this.crowdResponseTick = -10000;
    this.crowdResponseStrength = 0;
  }

  async init(ctx) {
    this.scene = ctx.render.scene;
    if (!this.scene) throw new Error('world: render scene is unavailable.');
    this.materials = ctx.systems.get('materials');
    this.scene.background = new THREE.Color(0x050911);
    this.scene.fog = new THREE.FogExp2(0x091722, 0.019);
    this.scene.add(this.group);
    this.bag.custom(() => this.scene && this.scene.remove(this.group));

    const worldRng = ctx.rng.fx.fork('world:rooftop-nine');
    this._buildSky();
    this._buildRoof(worldRng.fork('roof'));
    this._buildBoundary();
    this._buildMidground(worldRng.fork('midground'));
    this._buildCity(worldRng.fork('city'));
    this._buildNeon();
    this._buildCrowd(worldRng.fork('crowd'));
    this._buildRain(worldRng.fork('rain'));
    this._buildSteam(worldRng.fork('steam'));
    this._buildBanners();
    this._buildTraffic(worldRng.fork('traffic'));
    this._buildPracticalLights();
    this.applyQuality(ctx.config.quality);
    const offQuality = ctx.config.onChange('preset', () => this.applyQuality(ctx.config.quality));
    this.bag.custom(offQuality);

    const react = (tick, strength) => {
      if (strength < this.crowdResponseStrength
        && tick - this.crowdResponseTick < 36) return;
      this.crowdResponseTick = tick;
      this.crowdResponseStrength = clamp(strength, 0, 1);
    };
    const offHit = ctx.events.on('combat:hit', (p) => {
      if (p.counter || p.damage >= 30) react(p.tick, p.counter ? 0.85 : 0.56);
    });
    const offWall = ctx.events.on('combat:wall-splat', (p) => react(p.tick, 0.92));
    const offRound = ctx.events.on('combat:round-end', (p) => react(p.tick, 1));
    this.bag.custom(() => {
      offHit();
      offWall();
      offRound();
    });

    ctx.log.info(
      `world: rooftop arena ready (${CITY_COUNT} towers, ${CROWD_COUNT} crowd, ` +
      `${RAIN_COUNT} rain streaks)`,
    );
  }

  _mat(opts) {
    const m = new THREE.MeshStandardMaterial(opts);
    this.bag.material(m);
    return m;
  }

  _physical(opts) {
    const m = new THREE.MeshPhysicalMaterial(opts);
    this.bag.material(m);
    return m;
  }

  _surface(kind, opts) {
    return this.materials.material(kind, opts);
  }

  _geo(g) {
    this.bag.geometry(g);
    return g;
  }

  _buildSky() {
    const geo = this._geo(new THREE.SphereGeometry(180, 24, 14));
    const mat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: {
        topColor: { value: new THREE.Color(0x030711) },
        horizonColor: { value: new THREE.Color(0x163149) },
        storm: { value: 0 },
      },
      vertexShader: `
        varying vec3 vWorld;
        void main() {
          vec4 world = modelMatrix * vec4(position, 1.0);
          vWorld = world.xyz;
          gl_Position = projectionMatrix * viewMatrix * world;
        }
      `,
      fragmentShader: `
        varying vec3 vWorld;
        uniform vec3 topColor;
        uniform vec3 horizonColor;
        uniform float storm;
        void main() {
          float h = clamp(normalize(vWorld).y * 0.7 + 0.35, 0.0, 1.0);
          vec3 c = mix(horizonColor, topColor, pow(h, 0.62));
          c = mix(c, c * vec3(0.52, 0.68, 0.86), storm * 0.72);
          gl_FragColor = vec4(c, 1.0);
        }
      `,
    });
    this.bag.material(mat);
    const sky = new THREE.Mesh(geo, mat);
    sky.name = 'procedural-storm-sky';
    this.group.add(sky);
    this.skyMaterial = mat;
  }

  _buildRoof(rng) {
    // The structural slab is matte and receives character shadows; gloss comes from the puddle
    // layer. A specular material here turned the entire key-lit half of the arena into one white
    // highlight and erased foot readability.
    const wet = this._surface('concrete', {
      color: 0x10212a,
      packedMaps: false,
      name: 'rooftop-wet-concrete',
    });
    const slab = new THREE.Mesh(this._geo(new THREE.BoxGeometry(17, 0.36, 9.2)), wet);
    slab.position.y = -0.22;
    slab.receiveShadow = true;
    slab.castShadow = true;
    this.group.add(slab);

    // Concrete patchwork creates coherent roughness breaks without any texture asset.
    const patchMat = this._surface('concrete', {
      color: 0x1a313a,
      packedMaps: false,
      transparent: true,
      opacity: 0.2,
      depthWrite: false,
      name: 'rooftop-concrete-patches',
    });
    const patchGeo = this._geo(new THREE.PlaneGeometry(1, 1));
    const patches = new THREE.InstancedMesh(patchGeo, patchMat, 16);
    const patchDummy = new THREE.Object3D();
    for (let i = 0; i < 16; i++) {
      patchDummy.rotation.set(-Math.PI / 2, 0, rng.range(-0.12, 0.12));
      patchDummy.scale.set(rng.range(1.2, 3.0), rng.range(0.65, 1.65), 1);
      patchDummy.position.set(rng.range(-7.3, 7.3), 0.003, rng.range(-3.6, 3.3));
      patchDummy.updateMatrix();
      patches.setMatrixAt(i, patchDummy.matrix);
    }
    patches.instanceMatrix.needsUpdate = true;
    patches.receiveShadow = true;
    patches.name = 'rooftop-concrete-patches';
    this.group.add(patches);

    const puddleMat = this._physical({
      color: 0x16313b,
      roughness: 0.14,
      metalness: 0.08,
      clearcoat: 0.72,
      clearcoatRoughness: 0.08,
      transparent: true,
      opacity: 0.46,
      depthWrite: false,
    });
    const puddlePositions = [];
    const puddleNormals = [];
    const puddleUvs = [];
    const puddleIndices = [];
    const puddleRecords = [];
    let vertexOffset = 0;
    for (let i = 0; i < 11; i++) {
      const radius = rng.range(0.36, 1.05);
      const points = 18;
      const aspect = rng.range(0.42, 0.68);
      const edges = new Float32Array(points);
      for (let p = 0; p < points; p++) edges[p] = radius * rng.range(0.78, 1.16);
      const x = rng.range(-6.7, 6.7);
      const z = rng.range(-2.8, 2.8);
      puddleRecords.push({ x, z, radius, aspect });
      puddlePositions.push(x, 0.011, z);
      puddleNormals.push(0, 1, 0);
      puddleUvs.push(0.5, 0.5);
      for (let p = 0; p < points; p++) {
        const a = p / points * Math.PI * 2;
        const edge = edges[p];
        puddlePositions.push(x + Math.cos(a) * edge, 0.011, z + Math.sin(a) * edge * aspect);
        puddleNormals.push(0, 1, 0);
        puddleUvs.push(0.5 + Math.cos(a) * 0.5, 0.5 + Math.sin(a) * 0.5);
        puddleIndices.push(vertexOffset, vertexOffset + 1 + p, vertexOffset + 1 + ((p + 1) % points));
      }
      vertexOffset += points + 1;
    }
    const puddleGeo = this._geo(new THREE.BufferGeometry());
    puddleGeo.setAttribute('position', new THREE.Float32BufferAttribute(puddlePositions, 3));
    puddleGeo.setAttribute('normal', new THREE.Float32BufferAttribute(puddleNormals, 3));
    puddleGeo.setAttribute('uv', new THREE.Float32BufferAttribute(puddleUvs, 2));
    puddleGeo.setIndex(puddleIndices);
    puddleGeo.computeBoundingSphere();
    const puddles = new THREE.Mesh(puddleGeo, puddleMat);
    puddles.name = 'rooftop-puddle-field';
    puddles.renderOrder = 1;
    this.group.add(puddles);

    // A dim coherent catchlight outlines the same recorded water contours. It reads as surface
    // tension at gameplay distance without pretending to be a planar reflection.
    const puddleRimMat = new THREE.MeshBasicMaterial({
      color: 0x8ed8de,
      transparent: true,
      opacity: 0.018,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.bag.material(puddleRimMat);
    const puddleRimGeo = this._geo(new THREE.RingGeometry(0.86, 1, 24));
    const puddleRims = new THREE.InstancedMesh(puddleRimGeo, puddleRimMat, puddleRecords.length);
    const puddleDummy = new THREE.Object3D();
    for (let i = 0; i < puddleRecords.length; i++) {
      const record = puddleRecords[i];
      puddleDummy.position.set(record.x, 0.014, record.z);
      puddleDummy.rotation.set(-Math.PI / 2, 0, 0);
      puddleDummy.scale.set(record.radius, record.radius * record.aspect, 1);
      puddleDummy.updateMatrix();
      puddleRims.setMatrixAt(i, puddleDummy.matrix);
    }
    puddleRims.instanceMatrix.needsUpdate = true;
    puddleRims.renderOrder = 2;
    puddleRims.name = 'rooftop-puddle-catchlights';
    this.group.add(puddleRims);

    // Expansion joints and runoff cuts stay outside the central silhouette floor. They explain
    // why water collects near the drains without turning the combat plane into a busy grid.
    const seamMat = this._mat({ color: 0x071117, roughness: 0.92, metalness: 0.04 });
    const seamGeo = this._geo(new THREE.BoxGeometry(1, 0.012, 0.025));
    const seamData = [
      [-5.8, -2.38, 2.1, 0.03], [-3.25, -2.55, 1.45, -0.08], [3.4, -2.48, 1.7, 0.05],
      [5.65, -2.28, 2.25, -0.04], [-6.05, 2.1, 1.55, 0.09], [-3.7, 2.25, 1.9, -0.05],
      [3.75, 2.18, 1.65, 0.06], [6.0, 2.12, 1.7, -0.08],
    ];
    const seams = new THREE.InstancedMesh(seamGeo, seamMat, seamData.length);
    for (let i = 0; i < seamData.length; i++) {
      const [x, z, length, angle] = seamData[i];
      patchDummy.position.set(x, 0.013, z);
      patchDummy.rotation.set(0, angle, 0);
      patchDummy.scale.set(length, 1, 1);
      patchDummy.updateMatrix();
      seams.setMatrixAt(i, patchDummy.matrix);
    }
    seams.instanceMatrix.needsUpdate = true;
    seams.name = 'rooftop-runoff-seams';
    this.group.add(seams);

    // Luminous lane inlays frame the legal combat space without becoming a gameplay grid.
    const stripMat = this._mat({
      color: 0x2ccac2,
      roughness: 0.25,
      metalness: 0.55,
      emissive: 0x09a89f,
      emissiveIntensity: 1.7,
    });
    const stripGeoX = this._geo(new THREE.BoxGeometry(STAGE_HALF_WIDTH * 2, 0.018, 0.018));
    for (const z of [-STAGE_HALF_DEPTH - 0.22, STAGE_HALF_DEPTH + 0.22]) {
      const strip = new THREE.Mesh(stripGeoX, stripMat);
      strip.position.set(0, 0.018, z);
      this.group.add(strip);
    }
    const drainMat = this._mat({ color: 0x17262d, roughness: 0.62, metalness: 0.48 });
    const troughMat = this._mat({ color: 0x05090c, roughness: 0.98, metalness: 0.02 });
    const trough = new THREE.Mesh(this._geo(new THREE.BoxGeometry(10.25, 0.018, 0.84)), troughMat);
    trough.position.set(0, 0.009, 2.62);
    trough.name = 'rooftop-drain-trough';
    this.group.add(trough);
    const grateGeo = this._geo(new THREE.BoxGeometry(0.065, 0.028, 0.72));
    const barCount = 29;
    const grate = new THREE.InstancedMesh(grateGeo, drainMat, barCount);
    const grateDummy = new THREE.Object3D();
    for (let i = 0; i < barCount; i++) {
      grateDummy.position.set(-4.76 + i * 0.34, 0.025, 2.62);
      grateDummy.updateMatrix();
      grate.setMatrixAt(i, grateDummy.matrix);
    }
    grate.instanceMatrix.needsUpdate = true;
    grate.name = 'rooftop-drain-grate';
    this.group.add(grate);
  }

  _buildBoundary() {
    const steel = this._mat({ color: 0x394650, roughness: 0.32, metalness: 0.76 });
    const darkSteel = this._mat({ color: 0x10171d, roughness: 0.44, metalness: 0.63 });
    const postGeo = this._geo(new THREE.CylinderGeometry(0.075, 0.09, 1, 10));
    const railGeo = this._geo(new THREE.CylinderGeometry(0.035, 0.035, 1, 8));
    const posts = new THREE.InstancedMesh(postGeo, steel, 11);
    const rails = new THREE.InstancedMesh(railGeo, darkSteel, 9);
    const dummy = new THREE.Object3D();
    let postIndex = 0;
    let railIndex = 0;
    for (const side of [-1, 1]) {
      for (const z of [-2.65, 0, 2.65]) {
        dummy.position.set(side * (STAGE_HALF_WIDTH + 0.08), 1.31, z);
        dummy.rotation.set(0, 0, 0);
        dummy.scale.set(1, 2.65, 1);
        dummy.updateMatrix();
        posts.setMatrixAt(postIndex++, dummy.matrix);
      }
      for (const y of [0.44, 1.2, 2.22]) {
        dummy.position.set(side * (STAGE_HALF_WIDTH + 0.08), y, 0);
        dummy.rotation.set(Math.PI / 2, 0, 0);
        dummy.scale.set(1, 5.7, 1);
        dummy.updateMatrix();
        rails.setMatrixAt(railIndex++, dummy.matrix);
      }
    }
    // A lower back barrier anchors the spectators behind the legal depth edge without cutting
    // across fighter torsos. It shares the same two instanced draws as the side walls.
    for (const x of [-5.7, -2.85, 0, 2.85, 5.7]) {
      dummy.position.set(x, 0.5, -3.28);
      dummy.rotation.set(0, 0, 0);
      dummy.scale.set(0.82, 1.0, 0.82);
      dummy.updateMatrix();
      posts.setMatrixAt(postIndex++, dummy.matrix);
    }
    for (const y of [0.24, 0.54, 0.84]) {
      dummy.position.set(0, y, -3.28);
      dummy.rotation.set(0, 0, Math.PI / 2);
      dummy.scale.set(1, 12.1, 1);
      dummy.updateMatrix();
      rails.setMatrixAt(railIndex++, dummy.matrix);
    }
    posts.instanceMatrix.needsUpdate = true;
    rails.instanceMatrix.needsUpdate = true;
    posts.castShadow = true;
    rails.castShadow = true;
    posts.name = 'arena-boundary-posts';
    rails.name = 'arena-boundary-rails';
    this.group.add(posts, rails);

    // Orange impact beacons make each side wall immediately legible in peripheral vision.
    const beaconMat = this._mat({
      color: 0xff8b25,
      emissive: 0xff4b0c,
      emissiveIntensity: 2.4,
      roughness: 0.28,
    });
    const beaconGeo = this._geo(new THREE.BoxGeometry(0.17, 0.12, 0.42));
    const beacons = new THREE.InstancedMesh(beaconGeo, beaconMat, 6);
    let beaconIndex = 0;
    for (const side of [-1, 1]) {
      for (const y of [0.42, 1.28, 2.14]) {
        dummy.position.set(side * (STAGE_HALF_WIDTH + 0.08), y, 0);
        dummy.rotation.set(0, 0, 0);
        dummy.scale.set(1, 1, 1);
        dummy.updateMatrix();
        beacons.setMatrixAt(beaconIndex++, dummy.matrix);
      }
    }
    beacons.instanceMatrix.needsUpdate = true;
    beacons.name = 'arena-wall-beacons';
    this.group.add(beacons);
  }

  _buildMidground(rng) {
    // Large readable masses occupy the side lanes and leave a calm central notch behind the
    // fighters. They establish that the arena sits on a working roof instead of in front of a
    // flat city backdrop.
    const unitData = [
      [-7.15, 0.78, -5.35, 2.35, 1.56, 1.5],
      [-4.85, 0.55, -5.8, 1.45, 1.1, 1.25],
      [4.7, 0.62, -5.72, 1.65, 1.24, 1.3],
      [7.05, 0.92, -5.15, 2.1, 1.84, 1.65],
      [-2.7, 0.38, -6.2, 1.6, 0.76, 1.1],
      [2.65, 0.4, -6.15, 1.55, 0.8, 1.05],
    ];
    const equipmentGeo = this._geo(new THREE.BoxGeometry(1, 1, 1));
    const equipmentMat = this._mat({
      color: 0xffffff,
      roughness: 0.82,
      metalness: 0.18,
      vertexColors: true,
    });
    const equipment = new THREE.InstancedMesh(equipmentGeo, equipmentMat, unitData.length);
    const dummy = new THREE.Object3D();
    const color = new THREE.Color();
    for (let i = 0; i < unitData.length; i++) {
      const [x, y, z, w, h, d] = unitData[i];
      dummy.position.set(x, y, z);
      dummy.rotation.set(0, rng.range(-0.025, 0.025), 0);
      dummy.scale.set(w, h, d);
      dummy.updateMatrix();
      equipment.setMatrixAt(i, dummy.matrix);
      color.setHSL(i % 3 === 0 ? 0.08 : 0.57, 0.18, 0.065 + (i % 3) * 0.018);
      equipment.setColorAt(i, color);
    }
    equipment.instanceMatrix.needsUpdate = true;
    if (equipment.instanceColor) equipment.instanceColor.needsUpdate = true;
    equipment.name = 'midground-service-units';
    this.group.add(equipment);

    // Each cabinet owns one broad grille derived from the cabinet transform. Broad bands read as
    // ventilation at combat distance without adding fine noise behind faces and hands.
    const grilleGeo = this._geo(new THREE.BoxGeometry(1, 1, 0.025));
    const grilleMat = this._mat({
      color: 0x294a52,
      emissive: 0x0a2228,
      emissiveIntensity: 0.28,
      roughness: 0.56,
      metalness: 0.52,
    });
    const grilles = new THREE.InstancedMesh(grilleGeo, grilleMat, unitData.length * 3);
    let grilleIndex = 0;
    for (let i = 0; i < unitData.length; i++) {
      const [x, y, z, w, h, d] = unitData[i];
      for (let row = -1; row <= 1; row++) {
        dummy.position.set(x, y + row * h * 0.18, z + d * 0.505);
        dummy.rotation.set(0, 0, 0);
        dummy.scale.set(w * 0.56, h * 0.055, 1);
        dummy.updateMatrix();
        grilles.setMatrixAt(grilleIndex++, dummy.matrix);
      }
    }
    grilles.instanceMatrix.needsUpdate = true;
    grilles.name = 'midground-service-grilles';
    this.group.add(grilles);

    // Elevated transit/service decks cross the skyline at different depths. Their decreasing
    // value and thickness provide an immediate scale cue even when tower windows are dimmed.
    const deckData = [
      [-10.5, 4.2, -14, 10.5, 0.18, 0.34],
      [9.6, 5.4, -19, 12.5, 0.16, 0.3],
      [-7.5, 7.1, -28, 15.5, 0.12, 0.24],
    ];
    const deckGeo = this._geo(new THREE.BoxGeometry(1, 1, 1));
    const deckMat = this._mat({ color: 0x142b36, roughness: 0.58, metalness: 0.42 });
    const decks = new THREE.InstancedMesh(deckGeo, deckMat, deckData.length);
    for (let i = 0; i < deckData.length; i++) {
      const [x, y, z, w, h, d] = deckData[i];
      dummy.position.set(x, y, z);
      dummy.rotation.set(0, i % 2 ? -0.025 : 0.035, 0);
      dummy.scale.set(w, h, d);
      dummy.updateMatrix();
      decks.setMatrixAt(i, dummy.matrix);
    }
    decks.instanceMatrix.needsUpdate = true;
    decks.name = 'midground-transit-decks';
    this.group.add(decks);
  }

  _buildCity(rng) {
    const towerGeo = this._geo(new THREE.BoxGeometry(1, 1, 1));
    const towerMat = this._mat({
      // Instance colours already contain the façade value. Keep the base white or both colours
      // multiply into black and the attached windows appear to float in empty sky.
      color: 0xffffff,
      roughness: 0.56,
      metalness: 0.34,
      vertexColors: true,
    });
    const towers = new THREE.InstancedMesh(towerGeo, towerMat, CITY_COUNT);
    towers.name = 'procedural-city-towers';
    towers.castShadow = false;
    towers.receiveShadow = false;
    const dummy = new THREE.Object3D();
    const color = new THREE.Color();
    const towerRecords = [];
    for (let i = 0; i < CITY_COUNT; i++) {
      const lane = i < 36 ? -1 : 1;
      const z = i < 36 ? rng.range(-58, -13) : rng.range(-42, -11);
      let x = rng.range(-38, 38);
      if (Math.abs(x) < 7 && z > -20) x += x < 0 ? -8 : 8;
      const w = rng.range(1.3, 4.5);
      const d = rng.range(1.1, 3.8);
      const height = rng.range(5.5, 22) * (z < -35 ? 1.15 : 1);
      const rotation = rng.range(-0.08, 0.08);
      dummy.position.set(x, -3.2 + height * 0.5, z + lane * 0.2);
      dummy.scale.set(w, height, d);
      dummy.rotation.y = rotation;
      dummy.updateMatrix();
      towers.setMatrixAt(i, dummy.matrix);
      const depth = clamp((-z - 11) / 47, 0, 1);
      let lightness = 0.105 + (1 - depth) * 0.19 + rng.range(-0.018, 0.018);
      if (Math.abs(x) < 5.8 && z > -25) lightness *= 0.76;
      color.setHSL(
        rng.range(0.55, 0.63),
        rng.range(0.18, 0.36),
        clamp(lightness, 0.08, 0.31),
      );
      towers.setColorAt(i, color);
      towerRecords.push({ x, z: z + lane * 0.2, w, d, height, rotation });
    }
    towers.instanceMatrix.needsUpdate = true;
    if (towers.instanceColor) towers.instanceColor.needsUpdate = true;
    this.group.add(towers);

    // Every window band is derived from its actual tower transform. The previous independent RNG
    // pass produced luminous dashes floating in empty sky, which destroyed the skyline's depth
    // cues even though both the tower and window sets were individually plausible.
    const rowsPerTower = 4;
    const panesPerRow = 4;
    const windowCount = CITY_COUNT * rowsPerTower * panesPerRow;
    const windowGeo = this._geo(new THREE.BoxGeometry(1, 0.09, 0.025));
    const windowMat = this._mat({
      color: 0x72e6ff,
      emissive: 0x1b8fb3,
      emissiveIntensity: 0.82,
      roughness: 0.45,
      vertexColors: true,
    });
    const windows = new THREE.InstancedMesh(windowGeo, windowMat, windowCount);
    for (let i = 0; i < windowCount; i++) {
      const pane = i % panesPerRow;
      const row = Math.floor(i / panesPerRow) % rowsPerTower;
      const towerIndex = Math.floor(i / (rowsPerTower * panesPerRow));
      const tower = towerRecords[towerIndex];
      const facade = tower.d * 0.5 + 0.018;
      const localX = (pane - (panesPerRow - 1) * 0.5) * tower.w * 0.205;
      const dx = Math.cos(tower.rotation) * localX + Math.sin(tower.rotation) * facade;
      const dz = -Math.sin(tower.rotation) * localX + Math.cos(tower.rotation) * facade;
      const rowT = 0.22 + row * 0.165;
      const dark = ((towerIndex * 7 + row * 11) % 13) < 3;
      const depth = clamp((-tower.z - 11) / 47, 0, 1);
      dummy.position.set(
        tower.x + dx,
        -3.2 + tower.height * rowT,
        tower.z + dz,
      );
      dummy.scale.set(dark ? 0.001 : tower.w * 0.13, 1, 1);
      dummy.rotation.set(0, tower.rotation, 0);
      dummy.updateMatrix();
      windows.setMatrixAt(i, dummy.matrix);
      const warm = (towerIndex + row) % 5 === 0;
      color.setHex(warm ? 0xffa244 : (row % 2 ? 0x4bd9ff : 0x8adff2));
      color.multiplyScalar(0.48 + (1 - depth) * 0.52);
      windows.setColorAt(i, color);
    }
    windows.instanceMatrix.needsUpdate = true;
    if (windows.instanceColor) windows.instanceColor.needsUpdate = true;
    this.group.add(windows);

    // Dim façade edges reveal the building masses without putting high-frequency signage behind
    // the fighters. Like the window panes, they inherit each tower's real rotation and dimensions.
    const edgeCount = CITY_COUNT * 2;
    const edgeGeo = this._geo(new THREE.BoxGeometry(0.028, 1, 0.028));
    const edgeMat = this._mat({
      color: 0x4a8294,
      emissive: 0x163943,
      emissiveIntensity: 0.28,
      roughness: 0.5,
      vertexColors: true,
    });
    const edges = new THREE.InstancedMesh(edgeGeo, edgeMat, edgeCount);
    for (let i = 0; i < edgeCount; i++) {
      const towerIndex = Math.floor(i / 2);
      const side = i % 2 ? 1 : -1;
      const tower = towerRecords[towerIndex];
      const facade = tower.d * 0.5 + 0.026;
      const localX = side * tower.w * 0.48;
      const dx = Math.cos(tower.rotation) * localX + Math.sin(tower.rotation) * facade;
      const dz = -Math.sin(tower.rotation) * localX + Math.cos(tower.rotation) * facade;
      dummy.position.set(tower.x + dx, -3.2 + tower.height * 0.5, tower.z + dz);
      dummy.scale.set(1, tower.height * 0.88, 1);
      dummy.rotation.set(0, tower.rotation, 0);
      dummy.updateMatrix();
      edges.setMatrixAt(i, dummy.matrix);
      color.setHex(towerIndex % 7 === 0 ? 0xb77748 : 0x467a88);
      color.multiplyScalar(0.56 + (1 - clamp((-tower.z - 11) / 47, 0, 1)) * 0.44);
      edges.setColorAt(i, color);
    }
    edges.instanceMatrix.needsUpdate = true;
    if (edges.instanceColor) edges.instanceColor.needsUpdate = true;
    edges.name = 'procedural-city-facade-edges';
    this.group.add(edges);

    // Roof caps and antennae share those same tower records, so no equipment floats above an
    // unrelated building. All three detail sets remain one draw call each.
    const capCount = Math.ceil(CITY_COUNT / 3);
    const capGeo = this._geo(new THREE.BoxGeometry(1, 1, 1));
    const capMat = this._mat({ color: 0x243746, roughness: 0.46, metalness: 0.48 });
    const caps = new THREE.InstancedMesh(capGeo, capMat, capCount);
    for (let i = 0; i < capCount; i++) {
      const tower = towerRecords[(i * 3) % CITY_COUNT];
      dummy.position.set(tower.x, -3.2 + tower.height + 0.18, tower.z);
      dummy.scale.set(tower.w * 0.34, 0.36, tower.d * 0.38);
      dummy.rotation.set(0, tower.rotation, 0);
      dummy.updateMatrix();
      caps.setMatrixAt(i, dummy.matrix);
    }
    caps.instanceMatrix.needsUpdate = true;
    caps.name = 'procedural-city-roof-caps';
    this.group.add(caps);

    const antennaMat = this._mat({
      color: 0x293642, roughness: 0.38, metalness: 0.78,
      emissive: 0x30060a, emissiveIntensity: 0.25,
    });
    const antennaGeo = this._geo(new THREE.CylinderGeometry(0.025, 0.045, 2.8, 6));
    const antennaCount = 18;
    const antennas = new THREE.InstancedMesh(antennaGeo, antennaMat, antennaCount);
    for (let i = 0; i < 18; i++) {
      const tower = towerRecords[(i * 7 + 2) % CITY_COUNT];
      dummy.position.set(
        tower.x + rng.range(-tower.w * 0.16, tower.w * 0.16),
        -3.2 + tower.height + 1.4,
        tower.z,
      );
      dummy.scale.set(1, rng.range(0.7, 1.3), 1);
      dummy.rotation.set(0, 0, 0);
      dummy.updateMatrix();
      antennas.setMatrixAt(i, dummy.matrix);
    }
    antennas.instanceMatrix.needsUpdate = true;
    antennas.name = 'procedural-city-antennae';
    this.group.add(antennas);
  }

  _buildNeon() {
    const frameMat = this._mat({ color: 0x1a232c, roughness: 0.35, metalness: 0.76 });
    const backGeo = this._geo(new THREE.BoxGeometry(3.25, 1.48, 0.16));
    const barGeo = this._geo(new THREE.BoxGeometry(1, 1, 1));
    const makeSign = (x, y, z, hue, mirror = false) => {
      const sign = new THREE.Group();
      sign.position.set(x, y, z);
      sign.rotation.y = mirror ? -0.08 : 0.08;
      const glowMat = this._mat({
        color: hue,
        emissive: hue,
        emissiveIntensity: 3.1,
        roughness: 0.22,
      });
      const back = new THREE.Mesh(backGeo, frameMat);
      sign.add(back);
      // Original bracket-and-chevron tournament glyph; geometry only, no font or texture.
      const bars = [
        [-0.95, 0, 0.07, 0.14, 1.0, 0.08, 0],
        [0.95, 0, 0.07, 0.14, 1.0, 0.08, 0],
        [-0.62, 0.38, 0.1, 0.7, 0.1, 0.08, 0.42],
        [-0.62, -0.38, 0.1, 0.7, 0.1, 0.08, -0.42],
        [0.32, 0.25, 0.1, 0.9, 0.1, 0.08, -0.48],
        [0.32, -0.25, 0.1, 0.9, 0.1, 0.08, 0.48],
      ];
      const glyph = new THREE.InstancedMesh(barGeo, glowMat, bars.length);
      const dummy = new THREE.Object3D();
      for (let i = 0; i < bars.length; i++) {
        const [bx, by, bz, sx, sy, sz, rz] = bars[i];
        dummy.position.set(bx, by, bz);
        dummy.rotation.set(0, 0, rz);
        dummy.scale.set(sx, sy, sz);
        dummy.updateMatrix();
        glyph.setMatrixAt(i, dummy.matrix);
      }
      glyph.instanceMatrix.needsUpdate = true;
      glyph.name = mirror ? 'neon-glyph-sodium' : 'neon-glyph-cyan';
      sign.add(glyph);
      this.group.add(sign);
    };
    makeSign(-8.4, 4.6, -11, 0x2de7e1, false);
    makeSign(8.6, 5.5, -14, 0xff5b1e, true);
  }

  _buildCrowd(rng) {
    const bodyGeo = this._geo(new THREE.CapsuleGeometry(0.135, 0.44, 3, 6));
    const headGeo = this._geo(new THREE.SphereGeometry(0.105, 8, 6));
    const armGeo = this._geo(new THREE.CapsuleGeometry(0.043, 0.31, 2, 5));
    const jacketGeo = this._geo(new THREE.ConeGeometry(0.24, 0.32, 7, 1, true));
    const bodyMat = this._mat({
      color: 0xffffff,
      roughness: 0.84,
      metalness: 0,
      emissive: 0x06131a,
      emissiveIntensity: 0.22,
      vertexColors: true,
    });
    const headMat = this._mat({
      color: 0xffffff,
      roughness: 0.68,
      metalness: 0,
      vertexColors: true,
    });
    const bodies = new THREE.InstancedMesh(bodyGeo, bodyMat, CROWD_COUNT);
    const heads = new THREE.InstancedMesh(headGeo, headMat, CROWD_COUNT);
    const arms = new THREE.InstancedMesh(armGeo, bodyMat, CROWD_COUNT * 2);
    const jackets = new THREE.InstancedMesh(jacketGeo, bodyMat, CROWD_COUNT);
    const dummy = new THREE.Object3D();
    const color = new THREE.Color();
    for (let i = 0; i < CROWD_COUNT; i++) {
      const row = i % 3;
      const x = -7.6 + (i / (CROWD_COUNT - 1)) * 15.2 + rng.range(-0.24, 0.24);
      const scale = rng.range(0.82, 1.16);
      const z = -3.68 - row * 0.28 + rng.range(-0.07, 0.07);
      const rotation = rng.range(-0.35, 0.35);
      const jacket = i % 3 === 0 || i % 7 === 0;
      this.crowdRecords.push({
        x, z, scale, rotation, jacket, phase: rng.range(0, Math.PI * 2),
      });
      dummy.position.set(x, 0.47 * scale, z);
      dummy.scale.set(scale, scale, scale);
      dummy.rotation.set(0, rotation, 0);
      dummy.updateMatrix();
      bodies.setMatrixAt(i, dummy.matrix);
      color.setHSL(
        i % 5 === 0 ? 0.08 : 0.56 + (i % 4) * 0.018,
        i % 5 === 0 ? 0.3 : 0.23,
        0.075 + (i % 6) * 0.014,
      );
      bodies.setColorAt(i, color);
      dummy.position.set(x, 0.7 * scale, z + 0.006);
      dummy.scale.set(jacket ? scale : 0.001, jacket ? scale : 0.001, jacket ? scale : 0.001);
      dummy.rotation.set(0, rotation, Math.PI);
      dummy.updateMatrix();
      jackets.setMatrixAt(i, dummy.matrix);
      jackets.setColorAt(i, color);
      for (let side = 0; side < 2; side++) {
        const armIndex = i * 2 + side;
        dummy.position.set(x + (side ? 1 : -1) * 0.15 * scale, 0.57 * scale, z);
        dummy.scale.set(scale, scale, scale);
        dummy.rotation.set(0, rotation, (side ? 1 : -1) * 0.16);
        dummy.updateMatrix();
        arms.setMatrixAt(armIndex, dummy.matrix);
        arms.setColorAt(armIndex, color);
      }
      dummy.position.set(x, 0.94 * scale, z);
      dummy.scale.set(scale, scale, scale);
      dummy.rotation.set(0, rotation, 0);
      dummy.updateMatrix();
      heads.setMatrixAt(i, dummy.matrix);
      color.setHSL(0.045 + (i % 4) * 0.012, 0.22 + (i % 3) * 0.08, 0.18 + (i % 5) * 0.027);
      heads.setColorAt(i, color);
    }
    for (const mesh of [bodies, heads, arms, jackets]) {
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      this.group.add(mesh);
    }
    bodies.name = 'procedural-crowd-bodies';
    heads.name = 'procedural-crowd-heads';
    arms.name = 'procedural-crowd-arms';
    jackets.name = 'procedural-crowd-jackets';
    this.crowd = { bodies, heads, arms, jackets, dummy };
  }

  _updateCrowd(ctx) {
    if (!this.crowd) return;
    const elapsed = ctx.time.tick - this.crowdResponseTick;
    const response = elapsed >= 0 && elapsed < 84
      ? this.crowdResponseStrength * (1 - elapsed / 84)
      : 0;
    const time = ctx.time.simSeconds;
    const { bodies, heads, arms, jackets, dummy } = this.crowd;
    for (let i = 0; i < this.crowdRecords.length; i++) {
      const record = this.crowdRecords[i];
      const wave = Math.sin(time * 1.25 + record.phase);
      const stagger = 0.55 + ((i * 17) % 10) * 0.045;
      const cheer = response * stagger;
      const bounce = Math.abs(Math.sin(elapsed * 0.18 + record.phase)) * cheer * 0.055;
      const lean = wave * 0.028 + Math.sin(elapsed * 0.12 + record.phase) * cheer * 0.08;

      dummy.position.set(record.x, 0.47 * record.scale + bounce, record.z);
      dummy.scale.set(record.scale, record.scale, record.scale);
      dummy.rotation.set(0, record.rotation, lean);
      dummy.updateMatrix();
      bodies.setMatrixAt(i, dummy.matrix);

      dummy.position.set(record.x, 0.94 * record.scale + bounce, record.z);
      dummy.scale.set(record.scale, record.scale, record.scale);
      dummy.rotation.set(0, record.rotation + wave * 0.035, lean * 0.55);
      dummy.updateMatrix();
      heads.setMatrixAt(i, dummy.matrix);

      dummy.position.set(record.x, 0.7 * record.scale + bounce, record.z + 0.006);
      dummy.scale.set(
        record.jacket ? record.scale : 0.001,
        record.jacket ? record.scale : 0.001,
        record.jacket ? record.scale : 0.001,
      );
      dummy.rotation.set(0, record.rotation, Math.PI + lean * 0.45);
      dummy.updateMatrix();
      jackets.setMatrixAt(i, dummy.matrix);

      for (let side = 0; side < 2; side++) {
        const sign = side ? 1 : -1;
        dummy.position.set(
          record.x + sign * 0.15 * record.scale,
          (0.57 + cheer * 0.17) * record.scale + bounce,
          record.z,
        );
        dummy.scale.set(record.scale, record.scale, record.scale);
        dummy.rotation.set(0, record.rotation, sign * (0.16 + cheer * 1.03) + lean);
        dummy.updateMatrix();
        arms.setMatrixAt(i * 2 + side, dummy.matrix);
      }
    }
    bodies.instanceMatrix.needsUpdate = true;
    heads.instanceMatrix.needsUpdate = true;
    arms.instanceMatrix.needsUpdate = true;
    jackets.instanceMatrix.needsUpdate = true;
  }

  _buildRain(rng) {
    const positions = new Float32Array(RAIN_COUNT * 2 * 3);
    const base = new Float32Array(RAIN_COUNT * 4);
    for (let i = 0; i < RAIN_COUNT; i++) {
      base[i * 4] = rng.range(-18, 18);
      base[i * 4 + 1] = rng.range(0, 18);
      base[i * 4 + 2] = rng.range(-10, 8);
      base[i * 4 + 3] = rng.range(7.5, 13.5);
    }
    const geo = this._geo(new THREE.BufferGeometry());
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const mat = new THREE.LineBasicMaterial({
      color: 0x9ac9df,
      transparent: true,
      opacity: 0.31,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.bag.material(mat);
    const rain = new THREE.LineSegments(geo, mat);
    rain.frustumCulled = false;
    rain.renderOrder = 5;
    this.group.add(rain);
    this.rain = rain;
    this.rainPositions = positions;
    this.rainBase = base;
    this.rainAttribute = geo.getAttribute('position');

    const splashBase = new Float32Array(RAIN_SPLASH_COUNT * 4);
    const splashGeo = this._geo(new THREE.RingGeometry(0.42, 1, 8));
    const splashMat = new THREE.MeshBasicMaterial({
      color: 0x8bdde5,
      transparent: true,
      opacity: 0.16,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.bag.material(splashMat);
    const splashes = new THREE.InstancedMesh(splashGeo, splashMat, RAIN_SPLASH_COUNT);
    const splashDummy = new THREE.Object3D();
    for (let i = 0; i < RAIN_SPLASH_COUNT; i++) {
      splashBase[i * 4] = rng.range(-7.4, 7.4);
      splashBase[i * 4 + 1] = rng.range(-3.45, 3.3);
      splashBase[i * 4 + 2] = rng.next();
      splashBase[i * 4 + 3] = rng.range(0.68, 1.24);
      splashDummy.position.set(splashBase[i * 4], 0.017, splashBase[i * 4 + 1]);
      splashDummy.rotation.set(-Math.PI / 2, 0, 0);
      splashDummy.scale.setScalar(0.001);
      splashDummy.updateMatrix();
      splashes.setMatrixAt(i, splashDummy.matrix);
    }
    splashes.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    splashes.instanceMatrix.needsUpdate = true;
    splashes.frustumCulled = false;
    splashes.renderOrder = 3;
    splashes.name = 'rain-ground-splashes';
    this.group.add(splashes);
    this.rainSplashes = splashes;
    this.rainSplashBase = splashBase;
    this.rainSplashDummy = splashDummy;
  }

  _buildSteam(rng) {
    const positions = new Float32Array(STEAM_COUNT * 3);
    const base = new Float32Array(STEAM_COUNT * 4);
    for (let i = 0; i < STEAM_COUNT; i++) {
      const vent = STEAM_VENTS[i % STEAM_VENTS.length];
      base[i * 4] = vent[0];
      base[i * 4 + 1] = rng.range(0, 1);
      base[i * 4 + 2] = vent[1];
      base[i * 4 + 3] = rng.range(0.7, 1.45);
    }
    const geo = this._geo(new THREE.BufferGeometry());
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const mat = new THREE.PointsMaterial({
      color: 0xb8d3dc,
      size: 0.21,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.16,
      depthWrite: false,
      blending: THREE.NormalBlending,
    });
    this.bag.material(mat);
    const steam = new THREE.Points(geo, mat);
    steam.frustumCulled = false;
    this.group.add(steam);
    this.steam = steam;
    this.steamPositions = positions;
    this.steamBase = base;
    this.steamAttribute = geo.getAttribute('position');

    const ventMat = this._mat({ color: 0x27323a, roughness: 0.35, metalness: 0.8 });
    const ventGeo = this._geo(new THREE.CylinderGeometry(0.24, 0.3, 0.34, 10));
    const vents = new THREE.InstancedMesh(ventGeo, ventMat, STEAM_VENTS.length);
    const ventDummy = new THREE.Object3D();
    for (let i = 0; i < STEAM_VENTS.length; i++) {
      const [x, z] = STEAM_VENTS[i];
      ventDummy.position.set(x, 0.14, z);
      ventDummy.updateMatrix();
      vents.setMatrixAt(i, ventDummy.matrix);
    }
    vents.instanceMatrix.needsUpdate = true;
    vents.name = 'rooftop-steam-vents';
    this.group.add(vents);
  }

  _buildBanners() {
    const mat = new THREE.ShaderMaterial({
      side: THREE.DoubleSide,
      transparent: true,
      uniforms: {
        time: { value: 0 },
        storm: { value: 0 },
        base: { value: new THREE.Color(0x101b25) },
        stripe: { value: new THREE.Color(0x25cfc6) },
      },
      vertexShader: `
        uniform float time;
        uniform float storm;
        varying vec2 vUv;
        void main() {
          vUv = uv;
          vec3 p = position;
          float loose = pow(uv.y, 1.35);
          p.z += sin(time * 3.2 + uv.y * 7.0) * (0.05 + storm * 0.07) * loose;
          p.x += sin(time * 2.1 + uv.y * 4.0) * 0.025 * loose;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
        }
      `,
      fragmentShader: `
        varying vec2 vUv;
        uniform vec3 base;
        uniform vec3 stripe;
        void main() {
          float chevron = step(0.72, fract((vUv.x + vUv.y * 0.62) * 4.0));
          float edge = smoothstep(0.0, 0.05, vUv.x) * smoothstep(1.0, 0.95, vUv.x);
          gl_FragColor = vec4(mix(base, stripe, chevron * 0.72), edge * 0.92);
        }
      `,
    });
    this.bag.material(mat);
    this.bannerMaterials.push(mat);
    const geo = this._geo(new THREE.PlaneGeometry(1.2, 2.35, 9, 18));
    geo.translate(0, -1.15, 0);
    for (const side of [-1, 1]) {
      const banner = new THREE.Mesh(geo, mat);
      banner.position.set(side * 6.95, 3.45, -2.95);
      banner.rotation.y = side * 0.12;
      this.group.add(banner);
    }
  }

  _buildTraffic(rng) {
    const cyan = this._mat({
      color: 0x63ddff, emissive: 0x1688b5, emissiveIntensity: 2.8,
      roughness: 0.25,
    });
    const orange = this._mat({
      color: 0xff8d35, emissive: 0xd63e0a, emissiveIntensity: 2.8,
      roughness: 0.25,
    });
    const geo = this._geo(new THREE.BoxGeometry(0.46, 0.045, 0.045));
    for (let i = 0; i < 18; i++) {
      const light = new THREE.Mesh(geo, i % 3 === 0 ? orange : cyan);
      const z = rng.range(-48, -18);
      light.userData.baseX = rng.range(-24, 24);
      light.userData.speed = rng.range(0.7, 1.8) * (i % 2 ? 1 : -1);
      light.userData.span = rng.range(22, 38);
      light.position.set(light.userData.baseX, rng.range(0.2, 4.2), z);
      this.group.add(light);
      this.traffic.push(light);
    }
  }

  _buildPracticalLights() {
    const cyan = new THREE.PointLight(0x32d8e7, 5.5, 11, 2.0);
    cyan.position.set(-5.4, 3.3, -1.8);
    const sodium = new THREE.PointLight(0xff8d31, 6.5, 12, 2.0);
    sodium.position.set(5.5, 3.6, -1.4);
    const back = new THREE.PointLight(0x405dff, 4.5, 16, 2.0);
    back.position.set(0, 4.2, -5.5);
    for (const l of [cyan, sodium, back]) {
      l.castShadow = false;
      this.group.add(l);
      this.neonLights.push(l);
      this.bag.custom(() => l.dispose());
    }
    const lightning = new THREE.DirectionalLight(0xb7d9ff, 0);
    lightning.position.set(-8, 12, -10);
    this.group.add(lightning);
    this.lightning = lightning;
    this.bag.custom(() => lightning.dispose());

    // A code-authored forked bolt gives the final-round flash a visible origin. It stays in the
    // far-left skyline, away from the combat silhouettes, and only its opacity changes at runtime.
    const boltSegments = [
      [-3.55, 8.6, -2.95, 7.25],
      [-2.95, 7.25, -3.35, 6.1],
      [-3.35, 6.1, -2.7, 4.85],
      [-2.7, 4.85, -3.05, 3.4],
      [-3.08, 6.8, -2.2, 6.35],
      [-2.95, 5.35, -2.12, 4.92],
    ];
    const boltPositions = [];
    const boltIndices = [];
    for (let i = 0; i < boltSegments.length; i++) {
      const [x1, y1, x2, y2] = boltSegments[i];
      const length = Math.hypot(x2 - x1, y2 - y1) || 1;
      const px = -(y2 - y1) / length * 0.055;
      const py = (x2 - x1) / length * 0.055;
      const vertex = i * 4;
      boltPositions.push(
        x1 + px, y1 + py, -22,
        x1 - px, y1 - py, -22,
        x2 - px, y2 - py, -22,
        x2 + px, y2 + py, -22,
      );
      boltIndices.push(vertex, vertex + 1, vertex + 2, vertex, vertex + 2, vertex + 3);
    }
    const boltGeo = this._geo(new THREE.BufferGeometry());
    boltGeo.setAttribute('position', new THREE.Float32BufferAttribute(boltPositions, 3));
    boltGeo.setIndex(boltIndices);
    boltGeo.computeVertexNormals();
    const boltMat = new THREE.MeshBasicMaterial({
      color: 0xc8e7ff,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      depthTest: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    this.bag.material(boltMat);
    const bolt = new THREE.Mesh(boltGeo, boltMat);
    bolt.frustumCulled = false;
    bolt.renderOrder = 4;
    bolt.name = 'final-round-lightning-bolt';
    this.group.add(bolt);
    this.lightningBolt = bolt;
    this.lightningMaterial = boltMat;
  }

  update(dt, ctx) {
    const time = ctx.time.simSeconds;
    const storm = ctx.sim.match ? ctx.sim.match.storm : 0;
    if (this.skyMaterial) this.skyMaterial.uniforms.storm.value = storm;
    for (const mat of this.bannerMaterials) {
      mat.uniforms.time.value = time;
      mat.uniforms.storm.value = storm;
    }

    const rainScale = this.rainDensity * (ctx.config.get('reduceRain') ? 0.45 : 1);
    if (this.rain) {
      this.rain.visible = rainScale > 0.01;
      const p = this.rainPositions;
      const b = this.rainBase;
      const range = 18;
      for (let i = 0; i < RAIN_COUNT; i++) {
        const x0 = b[i * 4];
        const y0 = b[i * 4 + 1];
        const z0 = b[i * 4 + 2];
        const speed = b[i * 4 + 3] * (1 + storm * 0.35);
        const y = ((y0 - time * speed) % range + range) % range;
        const wind = Math.sin(time * 0.44 + z0 * 0.1) * (0.11 + storm * 0.18);
        const o = i * 6;
        p[o] = x0 + wind;
        p[o + 1] = y;
        p[o + 2] = z0;
        p[o + 3] = x0 + wind - 0.045 - storm * 0.05;
        p[o + 4] = y - (0.28 + storm * 0.16) * rainScale;
        p[o + 5] = z0 + 0.03;
      }
      this.rain.material.opacity = (0.24 + storm * 0.13) * rainScale;
      this.rainAttribute.needsUpdate = true;

      if (this.rainSplashes) {
        const splashBase = this.rainSplashBase;
        const splashDummy = this.rainSplashDummy;
        for (let i = 0; i < RAIN_SPLASH_COUNT; i++) {
          const phase = (splashBase[i * 4 + 2]
            + time * splashBase[i * 4 + 3] * (0.72 + storm * 0.3)) % 1;
          const active = phase < 0.18;
          const pulse = active ? Math.sin((phase / 0.18) * Math.PI) : 0;
          const radius = active ? (0.016 + pulse * (0.034 + storm * 0.016)) * rainScale : 0.001;
          splashDummy.position.set(splashBase[i * 4], 0.017, splashBase[i * 4 + 1]);
          splashDummy.rotation.set(-Math.PI / 2, 0, phase * 4.2);
          splashDummy.scale.set(radius, radius * 0.62, 1);
          splashDummy.updateMatrix();
          this.rainSplashes.setMatrixAt(i, splashDummy.matrix);
        }
        this.rainSplashes.material.opacity = (0.055 + storm * 0.055) * rainScale;
        this.rainSplashes.instanceMatrix.needsUpdate = true;
      }
    }

    if (this.steam) {
      const p = this.steamPositions;
      const b = this.steamBase;
      for (let i = 0; i < STEAM_COUNT; i++) {
        const phase = ((b[i * 4 + 1] + time * b[i * 4 + 3] * 0.28) % 1 + 1) % 1;
        const spread = phase * phase * 0.52;
        p[i * 3] = b[i * 4] + Math.sin(time * 0.7 + i * 1.7) * spread;
        p[i * 3 + 1] = 0.22 + phase * 2.25;
        p[i * 3 + 2] = b[i * 4 + 2] + Math.cos(time * 0.6 + i) * spread;
      }
      this.steamAttribute.needsUpdate = true;
      this.steam.material.opacity = 0.12 + storm * 0.08;
    }

    for (let i = 0; i < this.traffic.length; i++) {
      const light = this.traffic[i];
      const span = light.userData.span;
      let x = light.userData.baseX + time * light.userData.speed;
      x = ((x + span) % (span * 2) + span * 2) % (span * 2) - span;
      light.position.x = x;
    }

    this._updateCrowd(ctx);

    if (this.lightning) {
      // Deterministic paired flashes during the final-round storm.
      const phase = (time * 0.23) % 1;
      const flash = phase > 0.965 ? 1 - (phase - 0.965) / 0.035
        : phase > 0.925 && phase < 0.94 ? 1 - (phase - 0.925) / 0.015 : 0;
      this.lightning.intensity = flash * storm * 7;
      if (this.lightningMaterial) this.lightningMaterial.opacity = flash * storm * 0.88;
    }
    void dt;
  }

  resetVisuals(ctx) {
    this.crowdResponseTick = -10000;
    this.crowdResponseStrength = 0;
    // Named capture scenarios force combat state rather than replaying event history. Reconstruct
    // the same public-state reaction here so heavy hits, throws and wall states can still review
    // the crowd behavior deterministically in one presented frame.
    let stagedResponse = 0;
    for (const fighter of ctx.sim.fighters) {
      if (!fighter) continue;
      if (fighter.posture === 'splat' || fighter.state === 'wall-splat') {
        stagedResponse = Math.max(stagedResponse, 0.92);
      }
      if (fighter.move && fighter.state === 'attack-active') {
        const damage = fighter.move.damage || 0;
        stagedResponse = Math.max(
          stagedResponse,
          fighter.move.isThrow ? 0.56 : damage >= 40 ? 0.84 : damage >= 28 ? 0.58 : 0,
        );
      }
    }
    if (ctx.app.screen === 'results') stagedResponse = 1;
    if (stagedResponse > 0) {
      this.crowdResponseTick = Math.max(0, ctx.time.tick - 12);
      this.crowdResponseStrength = stagedResponse;
    }
    this.update(0, ctx);
  }

  applyQuality(preset) {
    this.rainDensity = clamp(preset.rainDensity, 0.2, 1.35);
  }

  dispose() {
    this.traffic.length = 0;
    this.bannerMaterials.length = 0;
    this.neonLights.length = 0;
    this.crowdRecords.length = 0;
    this.crowd = null;
    this.bag.disposeAll();
    this.group.clear();
    this.scene = null;
    this.materials = null;
    this.rain = null;
    this.rainSplashes = null;
    this.rainSplashBase = null;
    this.rainSplashDummy = null;
    this.steam = null;
    this.lightningBolt = null;
    this.lightningMaterial = null;
  }
}
